/** @format */
import React, { Fragment, ReactElement } from 'react';

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type Mark = { delimiter: string; type: string; tag: string };
// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type Location = { index: number };
// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type Node = { text: string; type?: string };

/**
 * This is the marks definition, if we wanted to support both strong
 * and em, we could add the following to MARKS:
 * `{ delimiter: '_', type: 'em', tag: 'em' }`
 */
const MARKS: Mark[] = [{ delimiter: '**', type: 'strong', tag: 'strong' }];

const findMark = (
    text: string,
    marks: Mark[],
    openMark: string | undefined
): (Mark & Location) | undefined => {
    for (const markType of marks) {
        const isOpenMarkAndNoMatch =
            openMark && openMark !== markType.delimiter;

        if (isOpenMarkAndNoMatch) {
            continue; // if we already have an open mark, we don't want to match another type
        }

        const index = text.indexOf(markType.delimiter);
        const isMark = index !== -1;

        if (isMark) {
            return { ...markType, index };
        }
    }

    return undefined;
};

/**
 * Parses text with markdown into a list of `Node`s. If we were
 * to handle this using regular expressions, we would probably
 * have to use `dangerouslySetInnerHTML`, which is not ideal even
 * for trusted users.
 * @default marks MARKS only supporting strong tags
 */
export const parseMarkdown = (text?: string, marks = MARKS): Node[] => {
    const result: Node[] = [];
    let currentNode: Node = { text: '' };
    let cursor = 0;
    let openMark: string | undefined;

    if (!text) {
        return [];
    }

    const setOpenMark = (foundMark: Mark) => {
        if (currentNode.text.length > 0) {
            result.push(currentNode);
        }
        currentNode = { text: '' };
        openMark = foundMark.delimiter;
    };

    const setCloseMark = (foundMark: Mark) => {
        currentNode.type = foundMark.type;
        if (currentNode.text.length > 0) {
            result.push(currentNode);
        }
        currentNode = { text: '' };
        openMark = undefined;
    };

    while (cursor < text.length) {
        const character = text[cursor];

        // this is a bit like our buffer, we keep adding to it until we find a delimiter
        currentNode.text += character;

        const foundMark = findMark(currentNode.text, marks, openMark);

        if (foundMark) {
            // remove delimiter from text
            currentNode.text = currentNode.text.slice(0, foundMark.index);

            if (openMark === foundMark.delimiter) {
                setCloseMark(foundMark);
            } else {
                setOpenMark(foundMark);
            }
        }

        cursor += 1;
    }

    // if we had a mismatched delimiter, we need to add it back
    if (openMark) {
        currentNode.text = openMark + currentNode.text;
    }

    // add any remaining text
    if (currentNode.text.length > 0) {
        result.push(currentNode);
    }

    return result;
};

/**
 * Takes a node array and renders it into ReactElement with requisite html tags.
 * @default marks MARKS only supporting strong tags
 */
export const renderNodes = (nodes: Node[], marks = MARKS): ReactElement => {
    return (
        <Fragment>
            {nodes.map((node, index) => {
                if (!node.type) {
                    // eslint-disable-next-line react/no-array-index-key -- Legacy code: do not use as an example.
                    return <Fragment key={index}>{node.text}</Fragment>;
                }

                const mark = marks.find(({ type }) => type === node.type);

                if (mark) {
                    return React.createElement(
                        mark.tag,
                        // eslint-disable-next-line react/no-array-index-key -- Legacy code: do not use as an example.
                        { key: index },
                        node.text
                    );
                }

                // eslint-disable-next-line react/no-array-index-key -- Legacy code: do not use as an example.
                return <Fragment key={index}>{node.text}</Fragment>;
            })}
        </Fragment>
    );
};

/**
 * Renders a string of markdown (tiny subset), as ReactElements with
 * requisite html tags.
 * @default marks MARKS only supporting strong tags
 */
export const renderMarkdown = (text?: string, marks = MARKS): ReactElement =>
    renderNodes(parseMarkdown(text, marks), marks);

/**
 * Removes markdown characters from a string
 * @default marks MARKS only supporting strong tags
 */
export const removeMarkdownCharacters = (
    text?: string,
    marks = MARKS
): string => {
    return (
        marks.reduce((escapedString, { delimiter }) => {
            return escapedString?.replaceAll(delimiter, '');
        }, text) ?? ''
    );
};
